import escapeHtml from "escape-html";
import type { EChartsOption } from "echarts";
import {
  buildTooltipStyle,
  buildTooltipValueStyle,
  donutRadius,
  DONUT_SLICE_GAP_DEGREES,
  IMPRESSION_FORMATTER,
} from "@/pages/CampaignOverviewPage/charts/chartHelpers";
import {
  getChartPalette,
  type ChartPalette,
} from "@/pages/CampaignOverviewPage/charts/chartPalette";
import {
  buildPillBarOption,
  formatCompactImpressions,
} from "@/pages/CampaignOverviewPage/charts/pillBarChart";
import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";

const DISPLAY_CHANNEL = "DISPLAY";
const OTHER_LABEL = TEXT.campaignOverview.labels.other;
const CHANNEL_LABEL_WIDTH = 130;

// Groups rows by strategy_channel, summing impressions and total_spend per channel.
const groupByChannel = (
  rows: PerformanceReportRow[]
): Map<string, { impressions: number; totalSpend: number }> =>
  rows.reduce((acc, row) => {
    const key = row.strategy_channel ?? OTHER_LABEL;
    const existing = acc.get(key) ?? { impressions: 0, totalSpend: 0 };
    // Round spend cents-precisely on each accumulation step to prevent
    // floating-point drift across many rows (e.g. 37984.408... instead of 37984.41).
    const rawSpend = existing.totalSpend + (row.total_spend ?? 0);
    return acc.set(key, {
      impressions: existing.impressions + (row.impressions ?? 0),
      totalSpend: Math.round(rawSpend * 100) / 100,
    });
  }, new Map<string, { impressions: number; totalSpend: number }>());

const MAX_CHANNEL_SLICES = 5;

interface ChannelSlice {
  // Flat swatch color for the legend — slices drawn with a gradient still
  // get a solid swatch.
  color: string;
  fill: string | ChartPalette["barGradientV"];
  name: string;
  share: number;
  value: number;
}

// Two slices (e.g. Video + Display) use the hue-family gradients: Display is
// the orange slice, everything else plum. Three or more slices are flat and
// follow the part-to-whole order, with anything past the sixth collapsed into
// a gray "Other".
export const buildChannelSlices = (
  rows: PerformanceReportRow[],
  isDark: boolean
): ChannelSlice[] => {
  const palette = getChartPalette(isDark);
  const sorted = [...groupByChannel(rows).entries()]
    .map(([name, { impressions }]) => ({ name, value: impressions }))
    .sort((sliceA, sliceB) => sliceB.value - sliceA.value);
  const total = sorted.reduce((sum, { value }) => sum + value, 0);
  const share = (value: number) => (total > 0 ? value / total : 0);

  if (sorted.length <= 2) {
    const isDisplay = sorted.map(
      ({ name }) => name.toUpperCase() === DISPLAY_CHANNEL
    );
    // Guarantee two slices never share a hue when neither is Display.
    const orangeIndex = isDisplay.includes(true)
      ? isDisplay.indexOf(true)
      : sorted.length - 1;
    return sorted.map(({ name, value }, index) => {
      const isOrange = sorted.length === 2 && index === orangeIndex;
      return {
        color: isOrange ? palette.orange : palette.primary,
        fill: isOrange ? palette.displayGradient : palette.barGradientV,
        name,
        share: share(value),
        value,
      };
    });
  }

  const visible = sorted.slice(0, MAX_CHANNEL_SLICES);
  const otherValue = sorted
    .slice(MAX_CHANNEL_SLICES)
    .reduce((sum, { value }) => sum + value, 0);
  const slices = visible.map(({ name, value }, index) => ({
    color: palette.partToWhole[index],
    fill: palette.partToWhole[index],
    name,
    share: share(value),
    value,
  }));
  return otherValue > 0
    ? [
        ...slices,
        {
          color: palette.other,
          fill: palette.other,
          name: OTHER_LABEL,
          share: share(otherValue),
          value: otherValue,
        },
      ]
    : slices;
};

// How many channels the horizontal bar chart draws — all of them, unlike the
// donut, which folds everything past the fifth into "Other".
export const countChannels = (rows: PerformanceReportRow[]): number =>
  groupByChannel(rows).size;

// Donut box sizes — must match the sizes ChannelBreakdownWidget renders at.
export const CHANNEL_DONUT_INLINE_PX = 128;
export const CHANNEL_DONUT_STACKED_PX = 220;

export const buildChannelDonutOption = (
  rows: PerformanceReportRow[],
  isDark: boolean,
  boxPx: number = CHANNEL_DONUT_INLINE_PX
): EChartsOption => {
  const valueStyle = buildTooltipValueStyle(isDark);
  const slices = buildChannelSlices(rows, isDark);

  return {
    series: [
      {
        data: slices.map(({ fill, name, value }) => ({
          itemStyle: { color: fill },
          name,
          value,
        })),
        label: { show: false },
        labelLine: { show: false },
        padAngle: DONUT_SLICE_GAP_DEGREES,
        radius: donutRadius(boxPx),
        startAngle: 90,
        type: "pie",
      },
    ],
    tooltip: {
      ...buildTooltipStyle(isDark),
      formatter: (params: unknown) => {
        const item = params as {
          marker: string;
          name: string;
          percent: number;
          value: number;
        };
        return `${item.marker}${escapeHtml(item.name)}: <b style="${valueStyle}">${IMPRESSION_FORMATTER.format(item.value)}</b> (${item.percent}%)`;
      },
      trigger: "item",
    },
  };
};

export const buildChannelBarOption = (
  rows: PerformanceReportRow[],
  isDark: boolean
): EChartsOption => {
  const sorted = [...groupByChannel(rows).entries()]
    .map(([label, { impressions }]) => ({ label, value: impressions }))
    .sort((rowA, rowB) => rowB.value - rowA.value);

  return buildPillBarOption({
    formatValue: formatCompactImpressions,
    isDark,
    labelWidth: CHANNEL_LABEL_WIDTH,
    rows: sorted,
  });
};
